import type { HTMLProps, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { Document, MerchantDetails } from '../../types';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import styles from './footer-details.module.css';

type FooterDetailsProps = FooterMerchantDetailsProps &
  FooterCompanyDetailsProps &
  FooterPaymentDetailsProps;

export function FooterDetails({ document, merchant }: FooterDetailsProps): ReactNode {
  return (
    <div className={styles.container}>
      <FooterMerchantDetails merchant={merchant} />
      <FooterCompanyDetails merchant={merchant} />
      <FooterPaymentDetails document={document} />
    </div>
  );
}

interface FooterMerchantDetailsProps {
  merchant: MerchantDetails;
}

function FooterMerchantDetails({ merchant }: FooterMerchantDetailsProps): ReactNode {
  const { formatMessage } = useIntl();
  const { displayLanguage } = usePdfPreviewContext();

  const dotSeparator = '\u00A0\u00B7\u00A0';

  const merchantDistrictCourt = merchant.districtCourt
    ? `${formatMessage({ id: 'pdf-preview.district-court' }, { language: displayLanguage })} ${merchant.districtCourt}`
    : null;
  const merchantAddress = `${merchant.address.zipCode} ${merchant.address.city}, ${merchant.address.countryCode}`;

  return (
    <Details data-testid="pdf-preview-de-footer-merchant-info">
      <Detail data-testid="pdf-preview-de-footer-merchant-footer-name">{merchant.legalName}</Detail>
      <Detail data-testid="pdf-preview-de-footer-merchant-footer-street">
        {merchant.address.streetAddress}
      </Detail>
      <Detail data-testid="pdf-preview-de-footer-merchant-footer-address">{merchantAddress}</Detail>
      <Detail data-testid="pdf-preview-de-footer-email">{merchant.contactEmail}</Detail>
      <Detail>
        <span>
          {merchantDistrictCourt ? (
            <span data-testid="pdf-preview-de-footer-district-court">{merchantDistrictCourt}</span>
          ) : null}
          {merchantDistrictCourt && merchant.commercialRegisterNumber ? dotSeparator : null}
          {merchant.commercialRegisterNumber ? (
            <span data-testid="pdf-preview-de-footer-commercial-number">
              {merchant.commercialRegisterNumber}
            </span>
          ) : null}
        </span>
      </Detail>
    </Details>
  );
}

interface FooterCompanyDetailsProps {
  merchant: MerchantDetails;
}

function FooterCompanyDetails({ merchant }: FooterCompanyDetailsProps): ReactNode {
  const { formatMessage } = useIntl();
  const { displayLanguage } = usePdfPreviewContext();

  const showCompanyDetails = merchant.companyLeadership || merchant.taxNumber || merchant.vatNumber;

  if (showCompanyDetails) {
    const companyLeadershipDetail = `${formatMessage({ id: 'pdf-preview.leadership' }, { language: displayLanguage })} ${merchant.companyLeadership}`;
    const taxNumberDetail = `${formatMessage({ id: 'pdf-preview.steuernummer' }, { language: displayLanguage })} ${merchant.taxNumber}`;
    const vatNumberDetail = `${formatMessage({ id: 'pdf-preview.ust-udnr' }, { language: displayLanguage })} ${merchant.vatNumber}`;
    return (
      <Details data-testid="pdf-preview-de-footer-company-info">
        {merchant.companyLeadership ? (
          <Detail data-testid="pdf-preview-de-footer-merchant-leadership">
            {companyLeadershipDetail}
          </Detail>
        ) : null}
        {merchant.taxNumber ? (
          <Detail data-testid="pdf-preview-de-footer-merchant-tax-number">{taxNumberDetail}</Detail>
        ) : null}
        {merchant.vatNumber ? (
          <Detail data-testid="pdf-preview-de-footer-merchant-vat-number">{vatNumberDetail}</Detail>
        ) : null}
      </Details>
    );
  }

  return null;
}

interface FooterPaymentDetailsProps {
  document: Document;
}

function FooterPaymentDetails({ document }: FooterPaymentDetailsProps): ReactNode {
  const { formatMessage } = useIntl();
  const { displayLanguage, type } = usePdfPreviewContext();

  if (type === 'invoice') {
    const isForeignCurrency = document.currency && document.currency !== 'EUR';

    const ibanDetail = `${formatMessage({ id: 'pdf-preview.iban' }, { language: displayLanguage })}: ${document.iban ?? ''}`;
    const bicDetail = `${formatMessage({ id: 'pdf-preview.bic' }, { language: displayLanguage })}: ${document.bic ?? ''}`;
    const intermediaryBicDetail = `${formatMessage({ id: 'pdf-preview.intermediary-bic.label' }, { language: displayLanguage })}: ${formatMessage({ id: 'pdf-preview.intermediary-bic.value' }, { language: displayLanguage })}`;
    const referenceDetail = `${formatMessage({ id: 'pdf-preview.reference' }, { language: displayLanguage })}: ${document.reference ?? ''}`;

    return (
      <Details data-testid="pdf-preview-de-footer-payment-details">
        <Detail data-testid="pdf-preview-de-footer-payment-details-beneficiary">
          {document.beneficiaryName}
        </Detail>
        <Detail data-testid="pdf-preview-de-footer-payment-details-iban">{ibanDetail}</Detail>
        {document.bic ? (
          <Detail data-testid="pdf-preview-de-footer-payment-details-bic">{bicDetail}</Detail>
        ) : null}
        {isForeignCurrency ? (
          <Detail data-testid="pdf-preview-de-footer-payment-details-intermediary-bic">
            {intermediaryBicDetail}
          </Detail>
        ) : null}
        <Detail data-testid="pdf-preview-de-footer-payment-details-reference">
          {referenceDetail}
        </Detail>
      </Details>
    );
  }

  return null;
}

function Details(props: HTMLProps<HTMLParagraphElement>): ReactNode {
  return <div {...props} className={styles.details} />;
}

function Detail(props: HTMLProps<HTMLParagraphElement>): ReactNode {
  return <p {...props} className="body-3" />;
}
